import { cartMutations } from '@theme/haarfen-cart-mutations';
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

export function londonBoundary(serverSeconds) {
  const parts = (seconds) => Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' }).formatToParts(new Date(seconds * 1000)).filter((p) => p.type !== 'literal').map((p) => [p.type, Number(p.value)]));
  const day = parts(serverSeconds);
  const target = Date.UTC(day.year, day.month - 1, day.day + 1) / 1000;
  let result = target;
  for (let i = 0; i < 3; i++) {
    const local = parts(result);
    result += target - Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second) / 1000;
  }
  return result;
}

export class CartFreshness {
  generation = 0;
  request = 0;
  pending = new Set();
  dirty = new Map();
  active = null;
  timer = null;
  scheduled = false;
  editFailure = false;
  bypass = null;
  deadlines = new WeakMap();

  constructor() {
    for (const type of [StandardEvents.cartLinesUpdate, StandardEvents.cartNoteUpdate, StandardEvents.cartDiscountUpdate]) {
      document.addEventListener(type, (event) => this.mutation(event), true);
    }
    document.addEventListener('haarfen:section-render', (event) => {
      if (this.components().some((c) => c.dataset.sectionId === event.detail.sectionId)) this.track(event.detail.promise);
    });
    document.addEventListener('input', (event) => {
      const input = event.target;
      if (!input.matches?.('cart-items-component input[name="updates[]"], cart-note textarea, textarea[name="note"]')) return;
      this.dirty.set(input, input.value);
      this.editFailure = false;
      this.invalidate();
      this.status('pending');
    }, true);
    document.addEventListener('quantity-selector:update', (event) => {
      if (!event.target.closest?.('cart-items-component')) return;
      const input = event.target.querySelector('input');
      if (input) this.dirty.set(input, input.value);
      this.invalidate();
      this.status('pending');
    }, true);
    document.addEventListener('click', (event) => this.checkoutClick(event), true);
    document.addEventListener('submit', (event) => this.checkoutSubmit(event), true);
    window.addEventListener('focus', () => { this.expireDue(); this.refresh('focus'); });
    window.addEventListener('pageshow', () => { this.expireDue(); this.refresh('return'); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') { this.expireDue(); this.refresh('return'); }
    });
    queueMicrotask(() => this.refresh('cold'));
  }

  components() { return [...document.querySelectorAll('cart-items-component[data-section-id]')]; }
  nodes(c) { return [...c.querySelectorAll('.haarfen-current-promise[data-item-key]')].filter((n) => n.dataset.itemKey); }
  context() { return window.location.href + '|' + (window.Shopify?.locale || '') + '|' + (window.Shopify?.country || ''); }
  fingerprint(c) {
    const rows = [...c.querySelectorAll('[data-key]')];
    const keys = rows.map((n) => n.dataset.key).sort();
    if (keys.some((k) => !k) || new Set(keys).size !== keys.length) return null;
    const nodes = this.nodes(c);
    if (nodes.length !== keys.length || new Set(nodes.map((n) => n.dataset.itemKey)).size !== keys.length) return null;
    if (nodes.some((n) => !keys.includes(n.dataset.itemKey))) return null;
    return nodes.map((n) => [n.dataset.itemKey, n.dataset.variantId, n.dataset.itemQuantity].join('|')).sort().join('\n');
  }
  cleanDirty() {
    for (const [input] of this.dirty) if (!input.isConnected) this.dirty.delete(input);
  }
  captureQuantityRecovery(key) {
    return { generation: this.generation, key, inputs: new Map([...this.dirty].filter(([input]) => input.closest?.('[data-key]')?.dataset.key === key)) };
  }
  settleQuantityRecovery(recovery) {
    if (recovery.generation !== this.generation) return false;
    for (const [input, value] of recovery.inputs) {
      if (this.dirty.get(input) !== value) continue;
      if (!input.isConnected) { this.dirty.delete(input); continue; }
      const row = input.closest('[data-key]');
      const current = row?.querySelector('.haarfen-current-promise');
      if (row?.dataset.key === recovery.key && current?.dataset.itemKey === recovery.key &&
          Number(input.value) === Number(current.dataset.itemQuantity) && input.value === input.defaultValue) this.dirty.delete(input);
    }
    if (!this.dirty.size) this.editFailure = false;
    return !this.dirty.size;
  }
  invalidate() {
    this.generation++;
    this.request++;
    this.active?.controller.abort();
    clearTimeout(this.timer);
  }
  mutation(event) {
    this.invalidate();
    this.status('pending');
    const inputs = new Map(this.dirty);
    if (event.promise) this.track(event.promise, () => {
      for (const [input, value] of inputs) {
        if (input.value === value && this.dirty.get(input) === value) this.dirty.delete(input);
      }
      this.editFailure = false;
    }, () => { if (this.dirty.size) this.editFailure = true; });
    else this.schedule();
  }
  track(promise, success, failure) {
    const tracked = Promise.resolve(promise);
    if (this.pending.has(tracked)) return;
    this.pending.add(tracked);
    tracked.then(success, failure || (() => {})).finally(() => {
      this.pending.delete(tracked);
      this.schedule();
    });
  }
  schedule() {
    if (this.scheduled) return;
    this.scheduled = true;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.scheduled = false;
      this.refresh('settlement');
    }));
  }
  status(kind) {
    for (const button of document.querySelectorAll('button[name="checkout"], input[name="checkout"]')) {
      if (kind !== 'current' && !button.disabled) { button.dataset.haarfenDisabled = 'true'; button.disabled = true; }
      if (kind === 'current' && button.dataset.haarfenDisabled === 'true') { button.disabled = false; delete button.dataset.haarfenDisabled; }
    }
    for (const c of this.components()) {
      c.dataset.haarfenRefresh = kind;
      const status = c.querySelector('[data-haarfen-refresh-status]');
      if (status) status.textContent = kind === 'current' ? '' : status.dataset[kind] || status.dataset.failed;
    }
  }
  expireDue() {
    let expired = false;
    for (const component of this.components()) for (const node of this.nodes(component)) {
      const deadline = this.deadlines.get(node);
      if (!deadline || Math.max(performance.now() - deadline.mono, Date.now() - deadline.wall, 0) < deadline.delay) continue;
      node.dataset.currentPurchaseState = 'out_of_stock';
      node.textContent = node.dataset.soldOut || '';
      node.hidden = false;
      expired = true;
    }
    if (expired) this.status('unavailable');
    return expired;
  }
  async refresh(reason = 'manual') {
    const context = this.context();
    this.cleanDirty();
    if (this.pending.size || this.dirty.size) {
      this.status(this.editFailure && !this.pending.size ? 'failed' : 'pending');
      return false;
    }
    if (this.active && this.active.generation === this.generation && this.active.context === context) return this.active.promise;
    this.active?.controller.abort();
    const generation = this.generation;
    const request = ++this.request;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    const current = () => request === this.request && generation === this.generation && context === this.context() && !this.pending.size && !this.dirty.size;
    this.status('pending');
    const promise = (async () => {
      try {
        const results = await Promise.all(this.components().map(async (component) => {
          const fingerprint = this.fingerprint(component);
          if (fingerprint === null) throw new Error('Missing current cart identity');
          const url = new URL(window.location.href);
          url.searchParams.set('_haarfen_refresh', crypto.randomUUID());
          const started = performance.now();
          const html = await sectionRenderer.getSectionHTML(component.dataset.sectionId, false, url, controller.signal);
          const elapsed = performance.now() - started;
          const clock = sectionRenderer.getSectionClock(component.dataset.sectionId, url);
          if (!(clock > 0)) throw new Error('Authoritative response time missing');
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.getElementById('shopify-section-' + component.dataset.sectionId)?.querySelector('cart-items-component[data-section-id="' + CSS.escape(component.dataset.sectionId) + '"]');
          if (!fresh || this.fingerprint(fresh) !== fingerprint) throw new Error('Cart response identity changed');
          const nodes = this.nodes(fresh);
          if (nodes.some((n) => !['available', 'preorder', 'out_of_stock', 'unavailable'].includes(n.dataset.currentPurchaseState) || !(Number(n.dataset.serverSeconds) > 0))) throw new Error('Incomplete current promise');
          const london = Object.fromEntries(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(clock * 1000)).filter((p) => p.type !== 'literal').map((p) => [p.type, p.value]));
          const today = `${london.year}-${london.month}-${london.day}`;
          if (nodes.some((n) => !n.dataset.promiseVersion && n.dataset.currentPurchaseState === 'preorder' && (!/^\d{4}-\d{2}-\d{2}$/.test(n.dataset.legacyDate || '') || n.dataset.legacyDate < today))) throw new Error('Compatibility date no longer current');
          if (nodes.some((n) => ['available', 'preorder'].includes(n.dataset.currentPurchaseState) && n.dataset.dueSeconds && (Number(n.dataset.dueSeconds) - Math.max(clock, Number(n.dataset.serverSeconds))) * 1000 <= elapsed)) throw new Error('Promise expired during refresh');
          return { component, fingerprint, nodes, elapsed, clock };
        }));
        if (!current() || results.some((r) => !r.component.isConnected || this.fingerprint(r.component) !== r.fingerprint)) return false;
        const migrated = await cartMutations.run(() => cartMutations.syncTiming(results.flatMap(result => result.nodes)));
        if (!current()) return false;
        if (migrated) {
          for (const component of this.components()) await sectionRenderer.renderSection(component.dataset.sectionId, { cache: false });
          this.schedule();
          return false;
        }
        let unavailable = false;
        let nextDue = Infinity;
        for (const { component, nodes, elapsed, clock } of results) {
          const byKey = new Map(nodes.map((n) => [n.dataset.itemKey, n]));
          for (const old of this.nodes(component)) {
            const fresh = byKey.get(old.dataset.itemKey);
            const server = Math.max(clock, Number(fresh.dataset.serverSeconds));
            const due = fresh.dataset.promiseVersion ? Number(fresh.dataset.dueSeconds) : londonBoundary(server);
            const delay = (due - server) * 1000 - elapsed;
            if (Number.isFinite(delay) && delay > 0) nextDue = Math.min(nextDue, delay);
            unavailable ||= !['available', 'preorder'].includes(fresh.dataset.currentPurchaseState);
            const replacement = document.importNode(fresh, true);
            if (Number.isFinite(delay) && delay > 0) this.deadlines.set(replacement, { mono: performance.now(), wall: Date.now(), delay });
            old.replaceWith(replacement);
          }
        }
        this.status(unavailable ? 'unavailable' : 'current');
        clearTimeout(this.timer);
        if (Number.isFinite(nextDue)) this.timer = setTimeout(() => { this.expireDue(); this.refresh('due'); }, Math.min(nextDue, 2147483647));
        return !unavailable;
      } catch (error) {
        if (current()) this.status('failed');
        return false;
      } finally {
        clearTimeout(timeout);
        if (this.active?.request === request) this.active = null;
      }
    })();
    this.active = { promise, controller, generation, context, request, reason };
    return promise;
  }
  async verifyCheckout() {
    while (this.pending.size) {
      await Promise.allSettled([...this.pending]);
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    }
    this.cleanDirty();
    return this.refresh('checkout');
  }
  checkoutClick(event) {
    const path = event.composedPath();
    if (path.some((n) => n.matches?.('shopify-accelerated-checkout-cart, .additional-checkout-buttons, .shopify-payment-button, .dynamic-checkout__content'))) {
      this.status('express');
      this.refresh('express');
      return;
    }
    const control = event.target.closest?.('a[href], button[name="checkout"], input[name="checkout"]');
    if (!control) return;
    if (control.matches('a')) {
      const url = new URL(control.href, window.location.href);
      if (!/\/checkout\/?$/.test(url.pathname) || url.origin !== window.location.origin) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.verifyCheckout().then((ok) => { if (ok) window.location.assign(url); });
    }
  }
  checkoutSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const submitter = event.submitter;
    if (this.bypass === form) { this.bypass = null; return; }
    if (submitter?.name !== 'checkout' && !/\/checkout\/?$/.test(new URL(form.action, window.location.href).pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.verifyCheckout().then((ok) => {
      if (!ok || !form.isConnected) return;
      this.bypass = form;
      form.requestSubmit(submitter);
      this.bypass = null;
    });
  }
}

export const cartFreshness = new CartFreshness();
